/**
 * Sales and inventory analytics (H2).
 *
 * **Money arrives as a number here, not as a string.** Everywhere else in this codebase a
 * NUMERIC is carried as text, because an invoice total that loses a paisa to a double is a
 * document nobody can reconcile. These figures are different in kind: they are sums over a
 * rollup, already rounded to two decimals server-side, and they exist to be plotted. A
 * chart axis takes a number, and converting a string to one in the render path of a
 * hundred-point series is work done a hundred times to reach the same value. The API
 * declares them `float` for the same reason; this file simply agrees with it.
 *
 * Nothing here is ever sent back, which is what makes that safe. There is no analytics
 * write path: the rollups are derived from the orders and the till, and the one POST in
 * the module rebuilds them from source rather than accepting a figure.
 *
 * **Every window is measured back from `as_of`, not from today.** `as_of` is the last day
 * the business traded. On a live system the two are the same date; on a demo database, a
 * staging copy or a system that closes on Sunday they are not, and a dashboard anchored on
 * the calendar would open on thirty empty days. Screens show the date rather than saying
 * "last 30 days" and leaving the reader to assume it ends today.
 */

import { API_PREFIX, apiGet } from '@/api/client'
import { formatMoney } from '@/lib/money'

const BASE = `${API_PREFIX}/analytics`

/** Where a sale came from. Kept apart all the way to the screen; see the API models. */
export const CHANNELS = ['order', 'pos'] as const

export type Channel = (typeof CHANNELS)[number]

export const CHANNEL_LABELS: Record<Channel, string> = {
  order: 'Sales orders',
  pos: 'Point of sale',
}

/** The figures every rollup slice carries. `margin_pct` is null when nothing was sold. */
export interface Measures {
  transactions: number
  units: number
  gross: number
  discount: number
  tax: number
  net: number
  cost: number
  margin: number
  margin_pct: number | null
}

export interface Delta {
  net: number
  margin: number
  transactions: number
  net_pct: number | null
}

export interface SeriesPoint extends Measures {
  day: string
}

export interface ChannelSlice extends Measures {
  channel: Channel
}

export interface SalesSummary {
  as_of: string
  days: number
  period_start: string
  period_end: string
  totals: Measures
  previous: Measures
  delta: Delta
  series: SeriesPoint[]
  channels: ChannelSlice[]
}

export interface RepAttainment {
  rep_id: string | null
  rep_name: string | null
  employee_code: string | null
  target: number
  achieved: number
  attainment_pct: number | null
}

export interface TargetAttainment {
  as_of: string
  month_start: string
  month_end: string
  /** How far through the month `as_of` is. A rep at 60% on day 20 of 30 is on pace. */
  month_elapsed_pct: number
  target: number
  achieved: number
  attainment_pct: number | null
  reps: RepAttainment[]
}

export interface ProductRow extends Measures {
  product_id: string
  product_code: string | null
  product_name: string | null
  category_name: string | null
  /** Units per trading day. The figure that compares across products of different ages. */
  velocity: number
}

export interface CategorySlice extends Measures {
  category_id: string | null
  category_name: string | null
}

export interface ProductPerformance {
  as_of: string
  days: number
  period_start: string
  period_end: string
  top: ProductRow[]
  bottom: ProductRow[]
  categories: CategorySlice[]
}

export interface AffinityRule {
  antecedent_id: string
  antecedent_name: string | null
  consequent_id: string
  consequent_name: string | null
  support: number
  confidence: number
  lift: number
}

export interface BasketAffinity {
  days: number
  baskets: number
  rules: AffinityRule[]
}

export interface CustomerRow {
  customer_id: string
  customer_name: string | null
  tier: string | null
  recency_days: number
  frequency: number
  monetary: number
  recency_score: number
  frequency_score: number
  monetary_score: number
  segment: string
  suggested_tier: string
  movement: 'up' | 'down' | 'hold'
}

export interface SegmentSlice {
  segment: string
  customers: number
  monetary: number
}

export interface CustomerAnalytics {
  as_of: string
  days: number
  customers: number
  segments: SegmentSlice[]
  movements: CustomerRow[]
  rows: CustomerRow[]
}

export interface CoverRow {
  product_id: string
  product_code: string | null
  product_name: string | null
  on_hand: number
  available: number
  daily_demand: number
  /** Null when nothing sold in the window: infinite cover, not zero. */
  days_of_cover: number | null
}

export interface InventoryHealth {
  as_of: string
  days: number
  turnover: number | null
  cogs: number
  inventory_value: number
  stockout_pct: number
  stockout_pairs: number
  tracked_pairs: number
  at_risk: CoverRow[]
  overstocked: CoverRow[]
}

export interface RebuildSummary {
  days: number
  rows: number
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export const getSalesSummary = (options: { days?: number; channel?: Channel | '' } = {}) =>
  apiGet<SalesSummary>(`${BASE}/sales${query({ days: options.days, channel: options.channel })}`)

export const getTargets = () => apiGet<TargetAttainment>(`${BASE}/sales/targets`)

export const getProducts = (options: { days?: number; limit?: number } = {}) =>
  apiGet<ProductPerformance>(`${BASE}/products${query(options)}`)

export const getAffinity = (options: { days?: number; limit?: number } = {}) =>
  apiGet<BasketAffinity>(`${BASE}/products/affinity${query(options)}`)

export const getCustomers = (options: { days?: number; limit?: number } = {}) =>
  apiGet<CustomerAnalytics>(`${BASE}/customers${query(options)}`)

export interface ChurnRow {
  customer_id: string
  customer_name: string
  risk: 'high' | 'medium' | 'low'
  /** 0-100; overdue-ness carries most of it, a falling spend trend the rest. */
  score: number
  days_since_last_order: number
  typical_gap_days: number
  revenue_last_90: number
  revenue_prior_90: number
  reasons: string[]
}

export interface ChurnRisk {
  as_of: string
  days: number
  customers: number
  high: number
  medium: number
  low: number
  revenue_at_risk: number
  rows: ChurnRow[]
}

export interface DiscountBand {
  band: string
  lines: number
  units: number
  units_per_line: number
  revenue: number
  discount_given: number
  margin_pct: number
}

export interface ProductLift {
  product_id: string
  product_name: string
  discounted_lines: number
  full_price_lines: number
  units_per_line_discounted: number
  units_per_line_full_price: number
  lift_pct: number
}

export interface PromotionEffectiveness {
  as_of: string
  days: number
  bands: DiscountBand[]
  overall_lift_pct: number | null
  discount_given: number
  best: ProductLift[]
  worst: ProductLift[]
}

export const getChurnRisk = (options: { days?: number; limit?: number } = {}) =>
  apiGet<ChurnRisk>(`${BASE}/churn${query(options)}`)

export const getPromotions = (options: { days?: number; limit?: number } = {}) =>
  apiGet<PromotionEffectiveness>(`${BASE}/promotions${query(options)}`)

export const getInventory = (options: { days?: number; limit?: number } = {}) =>
  apiGet<InventoryHealth>(`${BASE}/inventory${query(options)}`)

export const rebuildAnalytics = (days?: number) =>
  apiGet<RebuildSummary>(`${BASE}/rebuild${query({ days })}`, { method: 'POST' })

// --- windows ---------------------------------------------------------------------------

/** The windows every analytics screen offers. Bounded by the API at 730 days. */
export const WINDOWS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '12 months' },
] as const

// --- formatting ------------------------------------------------------------------------

export { formatMoney }

/**
 * A percentage the API already multiplied out: `12.5` -> `12.5%`.
 *
 * `null` is an em dash, never `0%`. Margin on a period with no revenue, growth against a
 * period that sold nothing, attainment against no target -- all three are "no figure", and
 * printing zero for them invents a fact.
 */
export function formatPct(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${value.toFixed(digits)}%`
}

/** The same, with a sign, for a change against the period before. */
export function formatDeltaPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`
}

/** Compact money for a chart axis: 12,50,000 is wider than the plot area. */
export function formatCompact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}k`
  return value.toFixed(0)
}

/** `"2026-03-10"` -> `10 Mar`. Dates from the API are plain dates, not timestamps. */
export function formatDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  })
}

export function formatFullDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** Whether a change is good news, for colour. Null when there is nothing to compare to. */
export function toneOf(value: number | null | undefined): 'up' | 'down' | 'flat' {
  if (value == null || !Number.isFinite(value) || value === 0) return 'flat'
  return value > 0 ? 'up' : 'down'
}

/** The five RFM segment names the API returns, in the order a marketer reads them. */
export const SEGMENT_ORDER = ['champions', 'loyal', 'promising', 'at risk', 'hibernating'] as const

export const MOVEMENT_LABELS: Record<CustomerRow['movement'], string> = {
  up: 'Promote',
  down: 'Demote',
  hold: 'Hold',
}
